import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { withTranslation } from "react-i18next"
import { Box } from "@mui/material"
import { Badge } from "reactstrap"
import { Link } from "react-router-dom"
import moment from "moment"
import ListDataTable from "../../components/Common/list-data-table"
import { apiBuscarFinDespesas } from "../../helpers/backend_helper"

const describeDate = dateStr => dateStr ? moment(dateStr).format("DD/MM/YY") : "N/D"

const formatCurrency = v => parseFloat(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" })

const situacaoPagamentoBadgeClass = (vencimentoStr, dataPagamentoStr) => {
  if(vencimentoStr) {
    if(dataPagamentoStr) return "success";
    const comparacao = moment(vencimentoStr);
    if(comparacao.isAfter(new Date())) return "warning";
    if(comparacao.isSameOrBefore(new Date())) return "danger";
  }
  return "primary";
}

const ContasPagarTable = forwardRef((props, refFather) => {
  const ref = useRef()

  const {
    t,
    filters,
    usePermissionEdit,
    usePermissionRemove,
    onEditar,
    onRemover,
    onPagar,
    onEstornar,
  } = props

  const [data, setData] = useState([])

  useImperativeHandle(refFather, () => ({
    search() {
      ref?.current?.search()
    }
  }))

  const colunas = useMemo(
    () => [
      {
        header: t("contaspagar.coluna.id_cloud"),
        accessorKey: "id",
        size: 100,
        enableHiding: false,
      },
      {
        header: t("contaspagar.coluna.seq"),
        accessorKey: "seq",
        size: 120,
        enableHiding: false,
      },
      {
        header: t("contaspagar.coluna.id_recorrencia"),
        accessorKey: "id_recorrencia",
        size: 120,
        enableHiding: false,
      },
      { header: t("contaspagar.coluna.loja"), accessorKey: "loja", size: 80 },
      {
        header: t("contaspagar.coluna.fornecedor"),
        accessorKey: "nome_fornecedor",
        size: 200,
      },
      {
        header: t("contaspagar.coluna.descricao"),
        accessorKey: "descricao",
        size: 200,
        filterFn: "contains",
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {row.original.id_recorrencia && (
              <i
                className="mdi mdi-repeat text-primary"
                title={t("contaspagar.recorrencia.icone_tooltip")}
              />
            )}
            <span>{row.original.descricao}</span>
          </Box>
        ),
      },
      {
        header: t("contaspagar.coluna.documento"),
        accessorKey: "documento",
        size: 100,
      },
      {
        header: t("contaspagar.coluna.emissao"),
        accessorKey: "emissao",
        size: 100,
        Cell: ({ cell }) => describeDate(cell.getValue()),
      },
      {
        header: t("contaspagar.coluna.vencimento"),
        accessorKey: "vencimento",
        size: 100,
        Cell: ({ cell, row }) => (
          <Badge
            className={
              "font-size-12 badge-soft-" +
              situacaoPagamentoBadgeClass(
                cell.getValue(),
                row.original.data_pagamento
              )
            }
            color={situacaoPagamentoBadgeClass(
              cell.getValue(),
              row.original.data_pagamento
            )}
            pill
          >
            {describeDate(cell.getValue())}
          </Badge>
        ),
      },
      {
        header: t("contaspagar.coluna.pagamento"),
        accessorKey: "data_pagamento",
        size: 100,
        Cell: ({ cell }) => describeDate(cell.getValue()),
      },
      {
        header: t("contaspagar.coluna.valor"),
        accessorKey: "valor_real",
        size: 120,
        Cell: ({ cell }) => (
          <div className="text-right">{formatCurrency(cell.getValue())}</div>
        ),
      },
      {
        header: t("contaspagar.coluna.codigo_conta"),
        accessorKey: "codigo_conta",
        size: 100,
      },
    ],
    [t]
  )

  return (
    <ListDataTable
      ref={ ref }
      searchFn={ params => apiBuscarFinDespesas(params) }
      advancedFilters={ filters }
      columns={ colunas }
      data={ data }
      setData={ setData }
      columnVisibility={ { seq: false, id_recorrencia: false } }
      defaultSorting={ [{ id: "vencimento", desc: false }] }
      enableRowActions={ true }
      renderRowActions={ (row, table) => {
        const conta = row.original
        const podeEditar = usePermissionEdit && conta.seq === 0
        const podeRemover = usePermissionRemove && conta.seq === 0
        const podePagar = usePermissionEdit && conta.seq === 0 && !conta.data_pagamento
        const podeEstornar = usePermissionEdit && conta.seq === 0 && conta.data_pagamento
        return (
          <Box sx={{ display: "flex", gap: "1rem" }}>
            { podeEditar && (
              <Link to="#" className="text-primary">
                <i className="mdi mdi-pencil font-size-18 me-3"
                   title={ t("contaspagar.acao.editar") }
                   onClick={ () => onEditar(conta) } />
              </Link>
            ) }
            { !podeEditar && usePermissionEdit && (
              <Link to="#" className="text-primary" style={{ pointerEvents: "none", opacity: 0.5 }}>
                <i className="mdi mdi-pencil font-size-18 me-3"
                   title={ t("contaspagar.acao.editar_bloqueado") } />
              </Link>
            ) }
            { podeRemover && (
              <Link to="#" className="text-danger">
                <i className="mdi mdi-delete font-size-18 me-3"
                   title={ t("contaspagar.acao.excluir") }
                   onClick={ () => onRemover(conta) } />
              </Link>
            ) }
            { !podeRemover && usePermissionRemove && (
              <Link to="#" className="text-danger" style={{ pointerEvents: "none", opacity: 0.5 }}>
                <i className="mdi mdi-delete font-size-18 me-3"
                   title={ t("contaspagar.acao.excluir_bloqueado") } />
              </Link>
            ) }
            { podePagar && (
              <Link to="#" className="text-success">
                <i className="mdi mdi-cash font-size-18 me-3"
                   title={ t("contaspagar.acao.pagar") }
                   onClick={ () => onPagar(conta) } />
              </Link>
            ) }
            { !podePagar && usePermissionEdit && conta.seq !== 0 && !conta.data_pagamento && (
              <Link to="#" className="text-success" style={{ pointerEvents: "none", opacity: 0.5 }}>
                <i className="mdi mdi-cash font-size-18 me-3"
                   title={ t("contaspagar.acao.pagar_bloqueado") } />
              </Link>
            ) }
            { podeEstornar && (
              <Link to="#" className="text-danger">
                <i className="mdi mdi-cash-remove font-size-18 me-3"
                   title={ t("contaspagar.acao.estornar") }
                   onClick={ () => onEstornar(conta) } />
              </Link>
            ) }
            { !podeEstornar && usePermissionEdit && conta.seq !== 0 && conta.data_pagamento && (
              <Link to="#" className="text-danger" style={{ pointerEvents: "none", opacity: 0.5 }}>
                <i className="mdi mdi-cash-remove font-size-18 me-3"
                   title={ t("contaspagar.acao.estornar_bloqueado") } />
              </Link>
            ) }
          </Box>
        )
      } }
    />
  )
})

ContasPagarTable.propTypes = {
  filters: PropTypes.array,
  usePermissionEdit: PropTypes.bool,
  usePermissionRemove: PropTypes.bool,
  onEditar: PropTypes.func,
  onRemover: PropTypes.func,
  onPagar: PropTypes.func,
  onEstornar: PropTypes.func,
}

export default withTranslation()(ContasPagarTable)
